// Select — presentation primitive. Native <select> wrapped in token-
// driven styling. Selector-free, dispatch-free; consumes its state via
// props only.
//
// Field-compatible: accepts the same id / aria-describedby /
// aria-invalid that Field injects via cloneElement, and forwards them
// to the inner <select>. See migration/phase13-primitive-stabilization-plan.md
// §2.1 for the architectural decision.
//
// Owns: the native <select>'s controlled value, options rendering, the
// optional placeholder-as-disabled-first-option pattern.
// Does NOT own: option fetching, popup styling, search, multi-select,
// virtualization, or any non-native dropdown behaviour.
//
// Props:
//   id:           string — input id (Field injects this)
//   name:         string — passed through
//   value:        string — controlled value
//   onChange:     (event) => void — receives the native change event
//   options:      Array<{ value, label }>
//   placeholder:  string — if provided AND value === "", renders a
//                          leading <option disabled hidden> so the
//                          trigger shows the placeholder text
//   disabled:     bool
//   className, ...rest

function Select({
  id,
  name,
  value,
  onChange,
  options = [],
  placeholder,
  disabled = false,
  className = '',
  ...rest
}) {
  const classes = [
    'ui-select',
    disabled ? 'ui-select--disabled' : '',
    className,
  ].filter(Boolean).join(' ');

  const showPlaceholder = Boolean(placeholder) && (value === '' || value == null);

  return (
    <select
      id={id}
      name={name}
      value={value ?? ''}
      onChange={onChange}
      disabled={disabled}
      className={classes}
      {...rest}
    >
      {showPlaceholder ? (
        <option value="" disabled hidden>
          {placeholder}
        </option>
      ) : null}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export default Select;
